import { useEffect } from 'preact/hooks';
import type { RefObject } from 'preact';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Contains keyboard focus inside `ref` while `active` (aria-modal dialogs must
 * actually trap DOM focus): moves focus in on activate, cycles Tab/Shift+Tab at
 * the edges, and restores focus to the previously-focused element on deactivate.
 * Activate on the transition's `show` flag — a `visibility:hidden` (pre-show)
 * container can't receive focus.
 */
export function useFocusTrap(ref: RefObject<HTMLElement>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;
    const prev = document.activeElement as HTMLElement | null;

    const focusables = () => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));

    // The enter transition starts computed-hidden; focus() no-ops on a hidden
    // element, so retry across a few frames until it takes.
    let raf = 0;
    const tryFocus = (attempts: number) => {
      const target = focusables()[0] ?? container;
      target.focus();
      if (document.activeElement !== target && attempts > 0) {
        raf = requestAnimationFrame(() => tryFocus(attempts - 1));
      }
    };
    tryFocus(10);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const current = document.activeElement;
      if (e.shiftKey && (current === first || current === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [ref, active]);
}
